import { Colors } from "@/enums/Colors";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { useTenantSelect } from "@/hooks/useTenantSelect";
import { AddOutlined, ExpandMore } from "@mui/icons-material";
import { Box, InputBase, ListSubheader, MenuItem, MenuProps, Select, Typography, menuItemClasses, styled } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import rocket from "../../../assets/images/rocket.svg";
import CommonButton from "../utils/CommonButton";
import LazoModal from "../utils/LazoModal";
import { LazoOneWorkspaceManagement } from "./LazoOneWorkspaceManagement";

const StyledCommonButton = styled(CommonButton)({
  borderRadius: "0.250rem",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  fontSize: "0.75rem",
  padding: "0.25rem 0",
});

const BootstrapInput = styled(InputBase)({
  "& .MuiSvgIcon-root": {
    color: "#fff",
  },
  "& .MuiInputBase-input": {
    padding: "0 !important",
    color: "#EADDFF",
    textAlign: "center",
    fontSize: "1rem",
    "&:focus": {
      borderColor: Colors.LAZO_PURPLE,
    },
  },
});

const PartialMenuProps: Partial<MenuProps> = {
  PaperProps: {
    sx: {
      marginTop: "0.25rem",
      color: "#fff",
      backgroundColor: "#000",
      border: `1px solid ${Colors.DARK_GREY_3E3E3E}`,
      borderRadius: "0.5rem",
      "& .MuiList-root": {
        "& .MuiMenuItem-root": {
          "&:hover": {
            backgroundColor: Colors.DARK_GREY_3E3E3E,
          },
          "&.Mui-selected": {
            backgroundColor: Colors.DARK_GREY_3E3E3E,
            "&:hover": {
              backgroundColor: Colors.DARK_GREY_3E3E3E,
            },
          },
        },
      },
    },
  },
};

export const OptionContainer = styled(Box)({
  overflow: "hidden",
  "& .MuiTypography-root": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

function TenantSelectComponent() {
  const { t } = useTranslation("navigation");

  const {
    workspaceState,
    items,
    tenantMemberships,
    ownedWorkspaces,
    hasPermissionsToAddCompany,
    handleChange,
    goToChat,
    onClickAddWorkSpace,
  } = useTenantSelect();

  const [openCreateSelectWorkspace, setOpenCreateSelectWorkspace] = useState(false);
  const [askYourKamOpen, setAskYourKamOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  function handleClickAddWorkSpace() {
    onClickAddWorkSpace();
    if (hasPermissionsToAddCompany && ownedWorkspaces.length > 0) {
      setOpenCreateSelectWorkspace(true);
    }
  }

  return (
    <>
      <Select
        value={
          items.flatMap((i) => i.tenants).find((t) => t.externalId === workspaceState.selectedTenantMembership.tenant.externalId)
            ?.externalId ?? ""
        }
        renderValue={(tenantExternalId) => {
          const tenant = items.flatMap((i) => i.tenants).find((t) => t.externalId === tenantExternalId);
          return (
            <Typography
              fontWeight={600}
              sx={{
                maxWidth: "100%",
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {tenant.name}
            </Typography>
          );
        }}
        onOpen={() => setDropdownOpen(true)}
        onClose={() => setDropdownOpen(false)}
        open={dropdownOpen}
        onChange={(e) => handleChange(e.target.value)}
        input={<BootstrapInput />}
        MenuProps={PartialMenuProps}
        IconComponent={ExpandMore}
        id="tenant-select"
      >
        {items.map((i) => {
          const normalizedWorkspaceSlug = StringUtils.capitalizeWords(i.workspaceSlug.replaceAll("-", " "));

          return [
            <ListSubheader
              key={`header-${i.workspaceSlug}`}
              sx={{
                backgroundColor: "#000",
                color: Colors.LAZO_PURPLE2,
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: "normal",
                padding: "6px 16px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#000",
                },
              }}
            >
              {t("tenantSelect.workspacePrefix")}
              {normalizedWorkspaceSlug}
            </ListSubheader>,
            ...i.tenants.map((t) => (
              <MenuItem
                key={t.externalId}
                value={t.externalId}
                sx={{ marginLeft: "1rem", borderLeft: "1px solid white", paddingX: "0.5rem", minHeight: "1.5rem" }}
              >
                <OptionContainer sx={{ width: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", justifyContent: "space-between" }}>
                    <Typography
                      fontWeight={600}
                      noWrap
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {t.name}
                    </Typography>
                  </Box>
                </OptionContainer>
              </MenuItem>
            )),
          ];
        })}
        {hasPermissionsToAddCompany && (
          // Add workspace button
          <Box className={`${menuItemClasses.root}`} sx={{ padding: "0.25rem 0.5rem" }}>
            <StyledCommonButton fullWidth onClick={handleClickAddWorkSpace}>
              <AddOutlined />
              {t("tenantSelect.companyWorkspace")}
            </StyledCommonButton>
          </Box>
        )}
      </Select>

      <LazoModal
        title=""
        open={askYourKamOpen}
        handleClose={() => setAskYourKamOpen(false)}
        buttons={
          <>
            <CommonButton sx={{ marginLeft: "25%", width: "50% !important" }} size="small" onClick={goToChat}>
              {t("tenantSelect.chatWithKam")}
            </CommonButton>
          </>
        }
      >
        <img src={rocket.src} alt="" />
        <Typography fontSize={".875rem"} fontWeight={400} color={Colors.DARKER_DARKER_GRAY} textAlign={"center"}>
          {t("tenantSelect.addWorkspacesContactKam")}
          <br /> {t("tenantSelect.pleaseContactKam")}
        </Typography>
      </LazoModal>
      <LazoOneWorkspaceManagement
        open={openCreateSelectWorkspace}
        setOpen={setOpenCreateSelectWorkspace}
        ownedWorkspaces={ownedWorkspaces}
        tenantMemberships={tenantMemberships}
      />
    </>
  );
}

export default React.memo(TenantSelectComponent);
